import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import {
  ECareerLandingPageTabs,
  PLACEHOLDER_IMAGE_AVATAR,
  thunkActionLoading,
} from '@configs'
import {
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { IPhotoMedia } from '@interfaces'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, TableProps, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import { ICareer } from 'src/interfaces/career-landing-management'
import {
  deleteCareerApplicationProcessAction,
  getCareerApplicationProcessAction,
  updateCareerApplicationProcessAction,
} from 'src/redux/actions/career-landing-management'

interface ITabItemProps {
  activeTab?: string
}

export const useApplicationProcess = ({ activeTab }: ITabItemProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation(['error', 'common'])
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)
  const [selectedRoadMap, setSelectedRoadMap] = useState<ICareer | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [dataSource, setDataSource] = useState<ICareer[]>([])
  const [itemMovedID, setItemMovedID] = useState<any>()
  const [selectedItemId, setSelectedItemId] = useState<string>('')

  const isDeleting = useAppSelector(
    (state) =>
      state.careerLanding.loadings[
        thunkActionLoading.DELETE_APPLICATION_LOADING
      ]
  )

  const applicationProcessList = useAppSelector(
    (state) => state.careerLanding.applicationProcess
  )
  const isGetting = useAppSelector(
    (state) =>
      state.careerLanding.loadings[thunkActionLoading.GET_APPLICATION_LOADING]
  )

  const onClose = () => {
    setShowModal(false)
    setSelectedRoadMap(null)
  }

  const onCloseConfirmModal = useCallback(() => {
    setOpenConfirmDeleteModal(false)
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
        delay: 250,
        tolerance: 5,
      },
    })
  )

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setItemMovedID(active.id)
      setDataSource((prev: any) => {
        const activeIndex = prev.findIndex((i: any) => i.id === active.id)
        const overIndex = prev.findIndex((i: any) => i.id === over?.id)
        return arrayMove(prev, activeIndex, overIndex)
      })
    }
  }

  const onDelete = async () => {
    try {
      if (selectedItemId) {
        const response = await dispatch(
          deleteCareerApplicationProcessAction(selectedItemId)
        ).unwrap()
        if (response?.success) {
          message.success(response.message)
          setOpenConfirmDeleteModal(false)
          dispatch(getCareerApplicationProcessAction())
        }
      } else {
      }
    } catch (error: any) {
      message.error(error.message)
    }
  }

  const columns: TableProps<ICareer>['columns'] = [
    {
      dataIndex: 'index',
      align: 'center',
      key: 'test',
      width: '4%',
    },
    {
      dataIndex: 'media',
      align: 'center',
      key: 'media',
      width: '4%',
      title: 'Image ',
      render: (record: IPhotoMedia) => {
        if (record) {
          if (['image/png', 'image/jpeg', 'image/jpg'].includes(record.type)) {
            return (
              <div className="flex items-center justify-center w-full">
                <img
                  src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
                  alt={record?.url}
                  className="object-cover w-14 h-14"
                />
              </div>
            )
          }

          return (
            <div className="flex items-center justify-center w-full">
              <div className="w-14 h-14">
                <ReactPlayer
                  url={record?.url}
                  controls={true}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
              </div>
            </div>
          )
        } else {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={PLACEHOLDER_IMAGE_AVATAR}
                alt={'placeholder'}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        }
      },
    },
    {
      dataIndex: 'mediaMobile',
      align: 'center',
      key: 'mediaMobile',
      width: '4%',
      title: 'Image Mobile',
      render: (record: IPhotoMedia) => {
        if (record) {
          if (['image/png', 'image/jpeg', 'image/jpg'].includes(record.type)) {
            return (
              <div className="flex items-center justify-center w-full">
                <img
                  src={record?.url ?? PLACEHOLDER_IMAGE_AVATAR}
                  alt={record?.url}
                  className="object-cover w-14 h-14"
                />
              </div>
            )
          }
          return (
            <div className="flex items-center justify-center w-full">
              <div className="w-14 h-14">
                <ReactPlayer
                  url={record?.url}
                  controls={true}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                />
              </div>
            </div>
          )
        } else {
          return (
            <div className="flex items-center justify-center w-full">
              <img
                src={PLACEHOLDER_IMAGE_AVATAR}
                alt={'placeholder'}
                className="object-cover w-14 h-14"
              />
            </div>
          )
        }
      },
    },
    {
      dataIndex: 'title',
      align: 'center',
      key: 'title',
      width: '4%',
      title: 'Title En',
      render: (record) => {
        return <p className="line-clamp-2">{record}</p>
      },
    },
    {
      dataIndex: 'titleInDutch',
      align: 'center',
      key: 'titleInDutch',
      width: '4%',
      title: 'Title Nl',
      render: (record) => {
        return <p className="line-clamp-2">{record}</p>
      },
    },
    {
      dataIndex: 'content',
      align: 'center',
      key: 'content',
      width: '4%',
      title: 'Content En',
      render: (record) => {
        return <p className="line-clamp-2">{record}</p>
      },
    },
    {
      dataIndex: 'contentInDutch',
      align: 'center',
      key: 'contentInDutch',
      width: '4%',
      title: 'Content Nl',
      render: (record) => {
        return <p className="line-clamp-2">{record}</p>
      },
    },
    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '4%',
      title: 'Action',
      render: (_, record: ICareer) => {
        return (
          <div className="flex space-x-4 items-f justify-center w-full">
            <Tooltip title={t('common:edit')}>
              <EditOutlined
                className="text-lg font-light mr-2.5 cursor-pointer !text-[#184f64] hover:opacity-80"
                onClick={() => {
                  setSelectedRoadMap(record)
                  setShowModal(true)
                }}
              />
            </Tooltip>

            <Tooltip title={'Delete'}>
              <DeleteOutlined
                className={`text-lg font-light mr-2.5 cursor-pointer  hover:opacity-80 !text-red-600`}
                onClick={() => {
                  setOpenConfirmDeleteModal(true)
                  setSelectedItemId(record?.id.toString())
                }}
              />
            </Tooltip>
          </div>
        )
      },
      ellipsis: true,
    },
  ]

  useEffect(() => {
    if (activeTab === ECareerLandingPageTabs.APPLICATION_PROCESS) {
      const getApplicationProcessList = async () => {
        await dispatch(getCareerApplicationProcessAction()).unwrap()
      }
      getApplicationProcessList()
    }
  }, [activeTab])

  useEffect(() => {
    if (applicationProcessList?.length) {
      const a = [...applicationProcessList]?.sort((a, b) => a.no - b.no)
      setDataSource(a)
    } else {
      setDataSource([])
    }
  }, [applicationProcessList])

  useEffect(() => {
    const updatePosition = async () => {
      if (itemMovedID) {
        try {
          const index = dataSource.findIndex((item) => item.id === itemMovedID)
          const res = await dispatch(
            updateCareerApplicationProcessAction({
              body: { no: index + 1 },
              id: itemMovedID,
            })
          ).unwrap()
          if (res.success) {
            message.success({
              content: res?.message ?? t('common:save_success'),
            })
          }
          setItemMovedID(null)
        } catch (error: any) {
          message.success({
            content: error?.message ?? t('common:save_success'),
          })
        }
      }
    }
    updatePosition()
  }, [itemMovedID])

  return {
    columns,
    setShowModal,
    sensors,
    onDragEnd,
    showModal,
    openConfirmDeleteModal,
    onCloseConfirmModal,
    applicationProcess: dataSource,
    onClose,
    isGetting,
    onDelete,
    isDeleting,
    selectedRoadMap,
  }
}
